<template>
  <ServicePage
    service-id="network"
    :monthly-cost="monthlyCost"
    :item-count="lines.length"
    :can-add="canAdd"
    @add="addLines"
  >
    <div class="space-y-6">
      <!-- IP addresses -->
      <ConfigSection
        section-key="ip"
        title="IP addresses"
        subtitle="Public IPv4 addresses"
        v-model:enabled="ip.enabled"
        :monthly-cost="cost(ipLine)"
      >
        <div class="space-y-5">
          <PriceTable :entries="ipEntries" selectable :selected="ip.plan" @select="ip.plan = $event" />
          <NumberField v-model="ip.count" label="Number of IP addresses" />
        </div>
      </ConfigSection>

      <!-- Traffic -->
      <ConfigSection
        section-key="traffic"
        title="Data transfer"
        subtitle="Outgoing premium traffic, inter-AZ traffic and bandwidth commitments"
        v-model:enabled="traffic.enabled"
        :monthly-cost="cost(trafficLine)"
      >
        <div class="space-y-5">
          <PriceTable :entries="trafficEntries" />

          <div class="grid grid-cols-1 md:grid-cols-3 gap-5">
            <NumberField
              v-model="traffic.outbound"
              label="Outbound data transfer"
              unit="GB/month"
              hint="Only outgoing traffic is charged. The first 200 GB are free."
            />
            <NumberField
              v-model="traffic.betweenAZ"
              label="Traffic between availability zones"
              unit="GB/month"
              hint="Charged for both directions."
            />
            <NumberField
              v-model="traffic.commitment"
              label="Guaranteed bandwidth commitment"
              unit="MBit/s"
            />
          </div>
        </div>
      </ConfigSection>

      <!-- Load balancing -->
      <ConfigSection
        section-key="loadbalancing"
        title="Load balancing"
        subtitle="Network load balancers charged per hour plus capacity units"
        v-model:enabled="loadbalancing.enabled"
        :monthly-cost="cost(loadBalancingLine)"
      >
        <div class="space-y-5">
          <PriceTable :entries="loadBalancingEntries" />
          <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
            <NumberField
              v-model="loadbalancing.count"
              label="Number of load balancers"
              hint="Charged per load balancer hour (730 hours per month)."
            />
            <NumberField
              v-model="loadbalancing.nlu"
              label="Average NLUs consumed"
              unit="NLU/hour"
              :hint="nluHint"
            />
          </div>
        </div>
      </ConfigSection>

      <!-- WAF -->
      <ConfigSection
        section-key="waf"
        title="Web Application Firewall"
        subtitle="Capacity, requests, rules and the PRO feature set"
        v-model:enabled="waf.enabled"
        :monthly-cost="cost(wafLine)"
      >
        <div class="space-y-5">
          <PriceTable :entries="wafEntries" />
          <div class="grid grid-cols-1 md:grid-cols-3 gap-5">
            <NumberField
              v-model="waf.mbits"
              label="WAF capacity"
              unit="MBit/s"
              hint="Each MBit/s includes 1 connection per second and 40 req/s."
            />
            <NumberField
              v-model="waf.requests"
              label="Requests"
              unit="million/month"
            />
            <NumberField
              v-model="waf.rules"
              label="WAF rules"
              :hint="`The first ${FREE_WAF_RULES} rules are included.`"
            />
          </div>
          <label class="flex items-center gap-3 text-sm text-slate-700">
            <input type="checkbox" v-model="waf.pro" class="w-4 h-4 rounded border-slate-300 text-indigo-600" />
            <span>
              PRO version
              <span v-if="wafPro" class="text-slate-500">
                — {{ wafPro.description }}
              </span>
            </span>
          </label>
        </div>
      </ConfigSection>

      <!-- VPN -->
      <ConfigSection
        section-key="vpn"
        title="VPN"
        subtitle="IPSec and GRE tunnel receivers"
        v-model:enabled="vpn.enabled"
        :monthly-cost="cost(vpnLine)"
      >
        <div class="space-y-5">
          <PriceTable :entries="vpnEntries" selectable :selected="vpn.plan" @select="vpn.plan = $event" />
          <NumberField v-model="vpn.count" label="Number of tunnels" />
        </div>
      </ConfigSection>

      <!-- NAT & DDoS (included) -->
      <ConfigSection
        section-key="included"
        title="NAT gateway & DDoS protection"
        subtitle="Included at no additional cost"
        v-model:enabled="included.enabled"
        :monthly-cost="0"
      >
        <div class="space-y-5">
          <PriceTable :entries="includedEntries" />
          <NumberField v-model="included.natGateways" label="Number of NAT gateways" />
          <p class="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-3">
            NAT gateways and DDoS protection are free of charge — they are listed in your estimate at
            no cost so the architecture stays visible.
          </p>
        </div>
      </ConfigSection>

      <!-- Private connect -->
      <ConfigSection
        section-key="privateConnect"
        title="Private Connect"
        subtitle="Dedicated private connection, individually quoted"
        v-model:enabled="privateConnect.enabled"
        :monthly-cost="0"
        :on-request="true"
      >
        <div class="space-y-4">
          <PriceTable :entries="privateConnectEntries" />
          <p class="text-sm text-indigo-800 bg-indigo-50 border border-indigo-200 rounded p-3">
            {{ privateConnectEntry?.description }}
          </p>
        </div>
      </ConfigSection>

      <CostPreview v-if="lines.length" :lines-prop="lines" />
    </div>
  </ServicePage>
</template>

<script>
import { mapGetters } from "vuex";
import ServicePage from "@/components/config/ServicePage.vue";
import ConfigSection from "@/components/config/ConfigSection.vue";
import NumberField from "@/components/config/NumberField.vue";
import PriceTable from "@/components/config/PriceTable.vue";
import CostPreview from "@/components/config/CostPreview.vue";
import { configurable } from "@/mixins/configurable";
import {
  HOURS_PER_MONTH,
  byType,
  estimateLine,
  lineItem,
  priceOf,
  round,
} from "@/utils/pricing";

// "Additional WAF rule, first 49 Rules are included"
const FREE_WAF_RULES = 49;
// Free allowance, then the next 10 TB, then the next 10 TB, then everything above.
const TRAFFIC_FREE_GB = 200;
const TRAFFIC_TIER1_LIMIT = 10200;
const TRAFFIC_TIER2_LIMIT = 20200;

export default {
  name: "NetworkService",
  components: { ServicePage, ConfigSection, NumberField, PriceTable, CostPreview },
  mixins: [configurable],
  data() {
    return {
      FREE_WAF_RULES,
      ip: { enabled: true, plan: null, count: 0 },
      traffic: { enabled: false, outbound: 0, betweenAZ: 0, commitment: 0 },
      loadbalancing: { enabled: false, count: 0, nlu: 0 },
      waf: { enabled: false, mbits: 0, requests: 0, rules: 0, pro: false },
      vpn: { enabled: false, plan: null, count: 0 },
      included: { enabled: false, natGateways: 0 },
      privateConnect: { enabled: false },
    };
  },
  computed: {
    ...mapGetters({ section: "pricing/section" }),

    ipEntries() {
      return this.section("network.ip") || [];
    },
    trafficEntries() {
      return this.section("network.traffic") || [];
    },
    vpnEntries() {
      return this.section("network.VPN") || [];
    },
    loadBalancingEntries() {
      return this.section("network.loadbalancing") || [];
    },
    wafEntries() {
      return this.section("network.waf") || [];
    },
    includedEntries() {
      return [
        ...(this.section("network.nat") || []),
        ...(this.section("network.ddos") || []),
      ];
    },
    privateConnectEntries() {
      return this.section("network.privateConnect") || [];
    },
    privateConnectEntry() {
      return this.privateConnectEntries[0];
    },
    wafPro() {
      return byType(this.wafEntries, "pro");
    },
    nluHint() {
      const nlu = byType(this.loadBalancingEntries, "nlu");
      return nlu ? nlu.description : "";
    },

    ipLine() {
      const { enabled, plan, count } = this.ip;
      if (!enabled || !plan || count <= 0) return null;

      // The on-demand rate is quoted per hour, the commitment rate per month.
      const perMonth = plan.unit === "hour" ? priceOf(plan) * HOURS_PER_MONTH : priceOf(plan);

      return estimateLine({
        key: "network.ip",
        serviceId: "network",
        name: "Network IP",
        description: plan.description || `${plan.type} IPv4 addresses`,
        config: { type: plan.type, count },
        items: [
          lineItem({
            label: `IPv4 (${plan.type})`,
            detail:
              plan.unit === "hour"
                ? `${count} address(es) × ${HOURS_PER_MONTH} hours`
                : `${count} address(es)`,
            quantity: count,
            unit: "month",
            unitPrice: perMonth,
          }),
        ],
      });
    },

    trafficLine() {
      const { enabled, outbound, betweenAZ, commitment } = this.traffic;
      if (!enabled) return null;

      const items = [];
      const amount = Math.max(0, Number(outbound) || 0);

      if (amount > 0) {
        const free = Math.min(amount, TRAFFIC_FREE_GB);
        const tier1 = Math.max(0, Math.min(amount, TRAFFIC_TIER1_LIMIT) - TRAFFIC_FREE_GB);
        const tier2 = Math.max(
          0,
          Math.min(amount, TRAFFIC_TIER2_LIMIT) - TRAFFIC_TIER1_LIMIT
        );
        const tier3 = Math.max(0, amount - TRAFFIC_TIER2_LIMIT);

        items.push(
          lineItem({
            label: "Data transfer out — first 200 GB",
            detail: `${round(free)} GB included`,
            quantity: free,
            unit: "GB",
            unitPrice: 0,
            cost: 0,
          })
        );
        if (tier1 > 0) {
          items.push(
            lineItem({
              label: "Data transfer out — 200 GB to 10 TB",
              detail: `${round(tier1)} GB`,
              quantity: tier1,
              unit: "GB",
              unitPrice: priceOf(byType(this.trafficEntries, "ondemand - 200GB-10TB")),
            })
          );
        }
        if (tier2 > 0) {
          items.push(
            lineItem({
              label: "Data transfer out — 10 to 20 TB",
              detail: `${round(tier2)} GB`,
              quantity: tier2,
              unit: "GB",
              unitPrice: priceOf(byType(this.trafficEntries, "ondemand - 10-20TB")),
            })
          );
        }
        if (tier3 > 0) {
          items.push(
            lineItem({
              label: "Data transfer out — above 20 TB",
              detail: `${round(tier3)} GB`,
              quantity: tier3,
              unit: "GB",
              unitPrice: priceOf(byType(this.trafficEntries, "ondemand - >20TB")),
            })
          );
        }
      }

      const az = Math.max(0, Number(betweenAZ) || 0);
      if (az > 0) {
        // Charged on both the sending and the receiving side.
        items.push(
          lineItem({
            label: "Traffic between availability zones",
            detail: `${round(az)} GB, charged in both directions`,
            quantity: az * 2,
            unit: "GB",
            unitPrice: priceOf(byType(this.trafficEntries, "betweenAZ")),
          })
        );
      }

      const mbit = Math.max(0, Number(commitment) || 0);
      if (mbit > 0) {
        items.push(
          lineItem({
            label: "Bandwidth commitment",
            detail: `${round(mbit)} MBit/s guaranteed`,
            quantity: mbit,
            unit: "MBit/s",
            unitPrice: priceOf(byType(this.trafficEntries, "mbitCommitment")),
          })
        );
      }

      if (!items.length) return null;

      return estimateLine({
        key: "network.traffic",
        serviceId: "network",
        name: "Network Traffic",
        description: "Outbound data transfer and bandwidth",
        config: { outbound: amount, betweenAZ: az, commitment: mbit },
        items,
      });
    },

    loadBalancingLine() {
      const { enabled, count, nlu } = this.loadbalancing;
      if (!enabled || count <= 0) return null;

      const hourly = byType(this.loadBalancingEntries, "hour");
      const nluEntry = byType(this.loadBalancingEntries, "nlu");
      const items = [
        lineItem({
          label: "Load balancer hours",
          detail: `${count} load balancer(s) × ${HOURS_PER_MONTH} hours`,
          quantity: count * HOURS_PER_MONTH,
          unit: "hour",
          unitPrice: priceOf(hourly),
        }),
      ];

      const units = Math.max(0, Number(nlu) || 0);
      if (units > 0) {
        items.push(
          lineItem({
            label: "Network load balancer capacity units",
            detail: `${round(units)} NLU × ${count} load balancer(s) × ${HOURS_PER_MONTH} hours`,
            quantity: units * count * HOURS_PER_MONTH,
            unit: "NLU/hour",
            unitPrice: priceOf(nluEntry),
          })
        );
      }

      return estimateLine({
        key: "network.loadbalancing",
        serviceId: "network",
        name: "Network Load Balancing",
        description: "Network load balancers",
        config: { count, nlu: units },
        items,
      });
    },

    wafLine() {
      const { enabled, mbits, requests, rules, pro } = this.waf;
      if (!enabled) return null;

      const items = [];
      const capacity = Math.max(0, Number(mbits) || 0);
      if (capacity > 0) {
        items.push(
          lineItem({
            label: "WAF capacity",
            detail: `${round(capacity)} MBit/s`,
            quantity: capacity,
            unit: "MBit/s",
            unitPrice: priceOf(byType(this.wafEntries, "mbits")),
          })
        );
      }

      const reqs = Math.max(0, Number(requests) || 0);
      if (reqs > 0) {
        items.push(
          lineItem({
            label: "WAF requests",
            detail: `${round(reqs)} million requests`,
            quantity: reqs,
            unit: "M requests",
            unitPrice: priceOf(byType(this.wafEntries, "requests")),
          })
        );
      }

      const billableRules = Math.max(0, (Number(rules) || 0) - FREE_WAF_RULES);
      if (billableRules > 0) {
        items.push(
          lineItem({
            label: "Additional WAF rules",
            detail: `${billableRules} rule(s) beyond the included ${FREE_WAF_RULES}`,
            quantity: billableRules,
            unit: "rule",
            unitPrice: priceOf(byType(this.wafEntries, "rule")),
          })
        );
      }

      if (pro) {
        items.push(
          lineItem({
            label: "WAF PRO",
            detail: this.wafPro?.description || "PRO feature set",
            quantity: 1,
            unit: "month",
            unitPrice: priceOf(this.wafPro),
          })
        );
      }

      if (!items.length) return null;

      return estimateLine({
        key: "network.waf",
        serviceId: "network",
        name: "Network WAF",
        description: "Web application firewall",
        config: { mbits: capacity, requests: reqs, rules: Number(rules) || 0, pro },
        items,
      });
    },

    vpnLine() {
      const { enabled, plan, count } = this.vpn;
      if (!enabled || !plan || count <= 0) return null;

      return estimateLine({
        key: "network.vpn",
        serviceId: "network",
        name: "Network VPN",
        description: plan.description || `${plan.type} tunnels`,
        config: { type: plan.type, count },
        items: [
          lineItem({
            label: `${plan.type.toUpperCase()} tunnel`,
            detail: `${count} tunnel(s)`,
            quantity: count,
            unit: "month",
            unitPrice: priceOf(plan),
          }),
        ],
      });
    },

    includedLine() {
      const { enabled, natGateways } = this.included;
      if (!enabled) return null;

      const nat = byType(this.section("network.nat"), "gateway");
      const ddos = byType(this.section("network.ddos"), "protection");
      const gateways = Math.max(0, Number(natGateways) || 0);

      const items = [];
      if (gateways > 0) {
        items.push(
          lineItem({
            label: "NAT gateways",
            detail: nat?.description || "Included",
            quantity: gateways,
            unit: "month",
            unitPrice: priceOf(nat),
          })
        );
      }
      items.push(
        lineItem({
          label: "DDoS protection",
          detail: ddos?.description || "Included",
          quantity: 1,
          unit: "month",
          unitPrice: priceOf(ddos),
        })
      );

      return estimateLine({
        key: "network.included",
        serviceId: "network",
        name: "Network NAT & DDoS protection",
        description: "Included at no additional cost",
        config: { natGateways: gateways },
        items,
      });
    },

    privateConnectLine() {
      if (!this.privateConnect.enabled || !this.privateConnectEntry) return null;

      return estimateLine({
        key: "network.privateConnect",
        serviceId: "network",
        name: "Network Private Connect",
        description: this.privateConnectEntry.description,
        config: { type: this.privateConnectEntry.type },
        items: [],
        onRequest: true,
      });
    },

    lines() {
      return [
        this.ipLine,
        this.trafficLine,
        this.loadBalancingLine,
        this.wafLine,
        this.vpnLine,
        this.includedLine,
        this.privateConnectLine,
      ].filter((line) => line && (line.monthlyCost > 0 || line.onRequest || line.items.length));
    },
  },
  watch: {
    ipEntries: {
      immediate: true,
      handler(list) {
        if (!this.ip.plan && list.length) this.ip.plan = list[0];
      },
    },
    vpnEntries: {
      immediate: true,
      handler(list) {
        if (!this.vpn.plan && list.length) this.vpn.plan = list[0];
      },
    },
  },
  methods: {
    cost(line) {
      return line ? line.monthlyCost : 0;
    },
  },
};
</script>
